import React, { useEffect, useRef, useState } from 'react';
import { hapticFeedback } from '../../../utils/telegram';
import './BottomSheet.css';

const BottomSheet = ({ isOpen, onClose, title, children, maxHeight = '80vh' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const sheetRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // iOS/Safari keyboard handling using VisualViewport
  useEffect(() => {
    if (!isOpen) return;
    const vv = window.visualViewport;
    const sheetEl = sheetRef.current;
    if (!vv || !sheetEl) return;

    const updateKeyboardInset = () => {
      // Calculate area covered by the keyboard
      const keyboardInset = Math.max(0, window.innerHeight - (vv.height + vv.offsetTop));
      sheetEl.style.setProperty('--keyboard-inset', `${keyboardInset}px`);
    };

    vv.addEventListener('resize', updateKeyboardInset);
    vv.addEventListener('scroll', updateKeyboardInset);
    updateKeyboardInset();

    return () => {
      vv.removeEventListener('resize', updateKeyboardInset);
      vv.removeEventListener('scroll', updateKeyboardInset);
      sheetEl.style.removeProperty('--keyboard-inset');
    };
  }, [isOpen]);

  // Ensure focused inputs are scrolled into view inside the sheet
  useEffect(() => {
    if (!isOpen) return;
    const contentEl = contentRef.current;
    if (!contentEl) return;

    const onFocusIn = (e) => {
      const target = e.target;
      if (!target) return;
      const tag = (target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') {
        // Delay a bit to let the keyboard animate
        setTimeout(() => {
          try {
            target.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
          } catch (_) {
            // Fallback manual scroll
            const targetRect = target.getBoundingClientRect();
            const contentRect = contentEl.getBoundingClientRect();
            const offset = (targetRect.top - contentRect.top) - contentEl.clientHeight / 2 + targetRect.height / 2;
            contentEl.scrollTop += offset;
          }
        }, 250);
      }
    };

    contentEl.addEventListener('focusin', onFocusIn);
    return () => {
      contentEl.removeEventListener('focusin', onFocusIn);
    };
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClose = () => {
    hapticFeedback.light();
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300); // Match animation duration
  };

  const handleTouchStart = (e) => {
    setStartY(e.touches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;

    const currentY = e.touches[0].clientY;
    const diff = currentY - startY;

    if (diff > 0) { // Only allow downward swipe
      setCurrentY(diff);
      if (sheetRef.current) {
        sheetRef.current.style.transform = `translateY(${diff}px)`;
      }
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    
    if (currentY > 100) { // If swiped down more than 100px, close
      handleClose();
    } else {
      // Reset position
      if (sheetRef.current) {
        sheetRef.current.style.transform = 'translateY(0)';
      }
    }
    
    setCurrentY(0);
  };

  if (!isOpen && !isVisible) return null;

  return (
    <div 
      className={`bottom-sheet-backdrop ${isVisible ? 'visible' : ''}`}
      onClick={handleBackdropClick}
    >
      <div 
        ref={sheetRef}
        className={`bottom-sheet ${isVisible ? 'visible' : ''}`}
        style={{ maxHeight }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="bottom-sheet-handle" />
        
        {title && (
          <div className="bottom-sheet-header">
            <h2 className="bottom-sheet-title">{title}</h2>
            <button 
              className="bottom-sheet-close"
              onClick={handleClose}
            >
              ×
            </button>
          </div>
        )}
        
        <div ref={contentRef} className="bottom-sheet-content">
          {children}
        </div>
      </div>
    </div>
  );
};

export default BottomSheet; 